import { useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { LiquidacionDePlataforma, PartnerDePlataforma } from '../../api/ops/backoffice';
import type { Mes } from '../../partners/logica';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Ventana } from '../../componentes/Ventana';
import { errorDeCampo } from '../../logica/acceso';
import { ERROR_MOTIVO, motivoValido, textoDeError } from '../negocios/logica';
import { ERROR_REFERENCIA, FORMAS, TEXTO_DATOS_LEGALES, cambiosDatosLegales, formDatosLegalesDe, referenciaValida, textosDecision, textosPago, type FormDatosLegales } from './logica';

/** Ventanas de Partners (RV Ventana del prototipo: `vapp` y `vlg`). */
interface PropsComunes {
    ops: Operaciones;
    movil: boolean;
    partner: PartnerDePlataforma;
    onCerrar: () => void;
}

/**
 * **Aprobar** o **rechazar** una solicitud (`decidePartnerApplication`). Al rechazar, «Motivo (opcional)» de hasta 500
 * letras, que va en el email (G1088).
 */
export function VentanaDecision({ ops, movil, partner, decision, onCerrar, onHecho }: PropsComunes & { decision: 'approve' | 'reject'; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [motivo, setMotivo] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [errorMotivo, setErrorMotivo] = useState<string | null>(null);
    const t = textosDecision(partner.legal_name, decision);
    const decidir = () => {
        if (ocupado) {
            return;
        }
        setOcupado(true);
        const m = motivo.trim();
        ops.decidePartnerApplication(partner.id, decision === 'reject' && m !== '' ? { decision, reason: m } : { decision })
            .then(() => {
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const delMotivo = errorDeCampo(e, 'reason');
                if (delMotivo !== null) {
                    setErrorMotivo(delMotivo);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <Ventana abierta titulo={t.titulo} texto={t.texto} accion={t.accion} accionIcono={decision === 'reject' ? 'x' : 'check'} ocupado={ocupado} movil={movil} onAccion={decidir} onCerrar={onCerrar}>
            {decision === 'reject' && (
                <Campo
                    etiqueta="Motivo (opcional)"
                    valor={motivo}
                    onChange={(v) => {
                        setMotivo(v.slice(0, 500));
                        setErrorMotivo(null);
                    }}
                    max={500}
                    error={errorMotivo}
                    ayuda="Va en el email. Máximo 500 caracteres."
                    onEnter={decidir}
                />
            )}
        </Ventana>
    );
}

/**
 * **Datos legales** de un partner (G237, `updatePlatformPartnerLegalData`): forma jurídica, razón social o nombre y NIF,
 * con motivo. Solo se manda lo que cambia; el NIF se ve enmascarado (G1096) y solo se manda si se escribe uno nuevo.
 */
export function VentanaDatosLegales({ ops, movil, partner, onCerrar, onHecho }: PropsComunes & { onHecho: (p: PartnerDePlataforma) => void }) {
    const avisar = useAvisoBreve();
    const [f, setF] = useState<FormDatosLegales>(() => formDatosLegalesDe(partner));
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errores, setErrores] = useState<Partial<Record<'nombre' | 'nif' | 'motivo', string>>>({});
    const errorMotivo = errores.motivo ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);
    const poner = (cambio: Partial<FormDatosLegales>) => {
        setF((x) => ({ ...x, ...cambio }));
        setErrores({});
    };
    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (!motivoValido(motivo)) {
            return;
        }
        setOcupado(true);
        ops.updatePlatformPartnerLegalData(partner.id, cambiosDatosLegales(partner, f, motivo))
            .then((nuevo) => {
                onHecho(nuevo);
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const de = { nombre: errorDeCampo(e, 'legal_name'), nif: errorDeCampo(e, 'tax_id'), motivo: errorDeCampo(e, 'reason') };
                if (de.nombre !== null || de.nif !== null || de.motivo !== null) {
                    setErrores({ nombre: de.nombre ?? undefined, nif: de.nif ?? undefined, motivo: de.motivo ?? undefined });
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <Ventana abierta titulo={`Datos legales de ${partner.legal_name}`} texto={TEXTO_DATOS_LEGALES} accion="Guardar" accionIcono="check" ocupado={ocupado} movil={movil} onAccion={guardar} onCerrar={onCerrar}>
            <Desplegable
                etiqueta="Forma jurídica"
                valor={f.forma}
                opciones={FORMAS}
                onChange={(v) => {
                    poner({ forma: v === 'self_employed' ? 'self_employed' : 'company' });
                }}
            />
            <Campo
                etiqueta="Razón social o nombre"
                valor={f.nombre}
                onChange={(v) => {
                    poner({ nombre: v });
                }}
                max={200}
                error={errores.nombre ?? null}
            />
            <Campo
                etiqueta="NIF"
                valor={f.nif}
                onChange={(v) => {
                    poner({ nif: v });
                }}
                mayusculas="characters"
                max={32}
                error={errores.nif ?? null}
            />
            <Campo
                etiqueta="Motivo"
                valor={motivo}
                onChange={(v) => {
                    setMotivo(v);
                    setErrores((x) => ({ ...x, motivo: undefined }));
                }}
                max={500}
                error={errorMotivo}
                ayuda="Obligatorio. Queda en Auditoría."
                onEnter={guardar}
            />
        </Ventana>
    );
}

/**
 * **Marcar pagada** (`markSettlementPaid`, prototipo `vpay`) con el campo obligatorio «Referencia de la transferencia»
 * (`payment_reference`, hasta 120 caracteres, G1146).
 */
// Provisional hasta C.66: el campo de la referencia, con el RV Campo de siempre, hasta que diseño lo dibuje.
export function VentanaPago({ ops, movil, liquidacion, mes, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; liquidacion: LiquidacionDePlataforma; mes: Mes; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [referencia, setReferencia] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const t = textosPago(liquidacion.partner_name, mes, liquidacion.total);
    const error = errorApi ?? (intentado && !referenciaValida(referencia) ? ERROR_REFERENCIA : null);
    const pagar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (!referenciaValida(referencia)) {
            return;
        }
        setOcupado(true);
        ops.markSettlementPaid(liquidacion.id, { payment_reference: referencia.trim() })
            .then(() => {
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const delCampo = errorDeCampo(e, 'payment_reference');
                if (delCampo !== null) {
                    setErrorApi(delCampo);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <Ventana abierta titulo={t.titulo} texto={t.texto} accion="Marcar pagada" accionIcono="wallet" ocupado={ocupado} movil={movil} onAccion={pagar} onCerrar={onCerrar}>
            <Campo
                etiqueta="Referencia de la transferencia"
                valor={referencia}
                onChange={(v) => {
                    setReferencia(v.slice(0, 120));
                    setErrorApi(null);
                }}
                max={120}
                error={error}
                ayuda="La del justificante del banco. Obligatoria."
                onEnter={pagar}
            />
        </Ventana>
    );
}
